import { create } from 'zustand'
import { persist } from 'zustand/middleware'

/**
 * App-owned UI preferences that must survive a refresh. Persisted to
 * localStorage via zustand's `persist` middleware.
 *
 * Note: theme is deliberately NOT here — it lives in ThemeProvider so the
 * no-flash inline script in index.html can read it synchronously before any JS
 * bundle (including this store) loads. Everything else persists here.
 */
/** Toggleable account-table columns (the "Account" column is always shown). */
export type AccountColumn =
  | 'visibility'
  | 'followers'
  | 'email'
  | 'phone'
  | 'website'
  | 'category'
  | 'business'
  | 'enriched'

/** Columns shown on a first visit (ADR-0003 Decision 14). */
export const DEFAULT_ACCOUNT_COLUMNS: AccountColumn[] = ['visibility', 'email', 'phone', 'website']

interface PreferencesState {
  /** Whether the dashboard sidebar is expanded. */
  sidebarOpen: boolean
  setSidebarOpen: (open: boolean) => void
  /** Which account columns are visible on the analysis detail page. Persisted
   * on-device — a per-device view preference, not shared data (Decision 14). */
  visibleAccountColumns: AccountColumn[]
  setAccountColumnVisible: (col: AccountColumn, visible: boolean) => void
}

export const usePreferences = create<PreferencesState>()(
  persist(
    (set) => ({
      sidebarOpen: true,
      setSidebarOpen: (open) => set({ sidebarOpen: open }),
      visibleAccountColumns: DEFAULT_ACCOUNT_COLUMNS,
      setAccountColumnVisible: (col, visible) =>
        set((s) => ({
          visibleAccountColumns: visible
            ? [...new Set([...s.visibleAccountColumns, col])]
            : s.visibleAccountColumns.filter((c) => c !== col),
        })),
    }),
    {
      name: 'warike-preferences',
      version: 1,
    },
  ),
)
